import axios from "axios";
import { useEffect, useRef, useState } from "react";
import Select, { components } from "react-select";
import { IoIosArrowDown as ArrowDown } from "react-icons/io";
import { MdOutlineEdit as EditIcon } from "react-icons/md";
import { RiEqualizerFill as Filter } from "react-icons/ri";
import { NotificationManager } from "react-notifications";
import Plus from "../../assets/svgs/Plus.svg";
import useAuth from "../../hooks/useAuth";
import { BASE_URL } from "../../utils/Element";
import { BoxCloseIcon } from "../../utils/icons";
import { handleDropdownClose } from "../../utils/utils";
import Loader from "../Loader";
import ManageSavedFiltersModal from "./ManageSavedFiltersModal";
import SaveSearchModal from "./SaveSearchModal";

const SavedFilterComponent = ({
  from,
  filterType,
  count,
  initialFilterData,
  isSidebarOpen,
  selectedSavefilterId,
  handleSelectFilter,
  saveSearchFilterData,
  onSetIsSidebarOpen,
  onCallApiAgain,
  onSetFilterData,
  onSetSelectedSaveFilterId,
  isMulti = false,
}) => {
  const [config] = useAuth();
  const filterDropdownRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [filterOptions, setFilterOptions] = useState([]);
  const [showManageModal, setShowManageModal] = useState(false);

  useEffect(() => {
    const handleClose = () => setIsOpen(false);
    handleDropdownClose(filterDropdownRef, handleClose);
  }, []);

  const fetchSavedSearch = () => {
    setLoading(true);
    axios
      .get(`${BASE_URL}/list-search?type=${from}`, config)
      .then((res) => {
        const data = res?.data?.search;

        // The user wants all saved filters to show in the dropdown, so we no longer separate them by type
        const filteredData = data;

        setFilterOptions(filteredData || []);
      })
      .catch((err) => {
        if (
          err.response?.data?.message &&
          err.response?.data?.message !== "Too Many Attempts."
        ) {
          NotificationManager.error(err.response?.data?.message);
        }
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchSavedSearch();
  }, [showModal, filterType]); // ✅ Added filterType as dependency

  const filterLabel =
    filterType === "advance" ? "Advance" : "Filter";

  const safeFrom = typeof from === "string" ? from : "";
  const lowerFrom = safeFrom.toLowerCase();

  const name =
    lowerFrom === "smartplan"
      ? "TouchPlan"
      : lowerFrom === "property"
        ? "Properties"
        : lowerFrom === "company"
          ? "Companies"
          : ["closed", "under contract", "contract-to-close"].includes(lowerFrom)
            ? safeFrom.charAt(0).toUpperCase() + safeFrom.slice(1)
            : safeFrom
              ? safeFrom.charAt(0).toUpperCase() + safeFrom.slice(1) + "s"
              : "";


  return (
    <>
      <div className="flex items-center gap-2">
        {count === 0 ? (
          <p
            role="button"
            className="head-5 green-H flex items-center gap-1"
            onClick={() => onSetIsSidebarOpen(!isSidebarOpen)}
          >
            {filterLabel} <Filter />
          </p>
        ) : (
          <div className="flex items-center gap-2">
            <p
              role="button"
              className="head-5 green-bg-H light-L flex items-center gap-1 px-4 py-2 rounded-[4px]"
              onClick={() => onSetIsSidebarOpen(!isSidebarOpen)}
            >
              {count} {count === 1 ? filterLabel : `${filterLabel}s`} <Filter />
            </p>

            <p
              role="button"
              className="head-5 green-H flex items-center gap-1"
              onClick={() => {
                onSetFilterData(initialFilterData);
                onCallApiAgain(initialFilterData);
                onSetSelectedSaveFilterId("");
                localStorage.removeItem("filterData");
                localStorage.removeItem("advanceFilterData"); // ✅ Clear advance filter too
                localStorage.removeItem("selectedSavefilterId");
              }}
            >
              Clear {filterLabel} <BoxCloseIcon />
            </p>

            {selectedSavefilterId === "" && (
              <button
                onClick={() => setShowModal(true)}
                className="add-contact-button green-bg-H light-L body-S"
                style={{ padding: "6px 12px" }}
              >
                <img className="mr-2" src={Plus} alt="plus" /> Save Filter
              </button>
            )}
          </div>
        )}

        {isMulti ? (
          <div className="w-44 xl:w-56">
            <Select
              isMulti
              placeholder="Saved Filters"
              options={filterOptions?.sort((a, b) => (a?.name || "").localeCompare(b?.name || ""))?.map(el => ({
                value: el.id,
                label: el.name,
                searches: el.searches
              })) || []}
              value={(filterOptions || []).filter(opt => (selectedSavefilterId ? String(selectedSavefilterId).split(",").includes(String(opt.id)) : false)).map(el => ({
                value: el.id,
                label: el.name,
                searches: el.searches
              }))}
              menuPortalTarget={document.body}
              styles={{
                menuPortal: (provided) => ({ ...provided, zIndex: 9999 })
              }}
              onChange={(selectedOptions) => {
                const newSelected = selectedOptions ? selectedOptions.map(opt => String(opt.value)) : [];
                const newSelectedString = newSelected.join(",");
                
                onSetSelectedSaveFilterId(newSelectedString);
                localStorage.setItem("selectedSavefilterId", newSelectedString);

                const combinedFilters = [];
                selectedOptions?.forEach(opt => {
                   if (opt.searches?.advance_filters) {
                     combinedFilters.push(...opt.searches.advance_filters);
                   } else if (Array.isArray(opt.searches)) {
                     combinedFilters.push(...opt.searches);
                   } else if (opt.searches) {
                     combinedFilters.push(opt.searches);
                   }
                });
                
                if (newSelected.length === 0) {
                  onSetFilterData(initialFilterData);
                  onCallApiAgain(initialFilterData);
                  localStorage.removeItem("filterData");
                  localStorage.removeItem("advanceFilterData");
                } else {
                  handleSelectFilter({ advance_filters: combinedFilters });
                }
              }}
              components={{
                MenuList: (props) => (
                  <components.MenuList {...props}>
                    <div
                      role="button"
                      onClick={() => setShowManageModal(true)}
                      className="flex items-center gap-1 green-H head-6 w-fit p-2 cursor-pointer"
                    >
                      <EditIcon size={18} />
                      Manage Saved Filters
                    </div>
                    <hr className="mt-1 mb-1" />
                    {props.children}
                  </components.MenuList>
                )
              }}
              className="body-N"
            />
          </div>
        ) : (
          <div ref={filterDropdownRef} className="custom-dropdown">
            <div
              role="button"
              style={{ border: "1px solid rgb(216, 216, 216)", height: "36px" }}
              className="select-header-input capitalize light-bg-L body-S dark-M flex justify-between items-center w-44"
              onClick={() => setIsOpen(!isOpen)}
            >
              <p>
                {!selectedSavefilterId
                  ? `Saved Filters`
                  : filterOptions?.find((el) => el?.id == selectedSavefilterId)
                    ?.name ?? "Saved Filters"}
              </p>
              <ArrowDown />
            </div>

            {isOpen && (
              <div
                className="dropdown-list-container p-2 shadow light-bg-L dark-M body-N rounded-box"
                style={{ width: "300px" }}
              >
                <div
                  role="button"
                  onClick={() => {
                    setShowManageModal(true);
                    setIsOpen(false);
                  }}
                  className="flex items-center gap-1 green-H head-6 w-fit"
                >
                  <EditIcon size={18} />
                  Manage Saved Filters
                </div>

                <hr className="mt-3 mb-2" />

                {loading ? (
                  <Loader />
                ) : (
                  <ul className="max-h-[240px] overflow-y-auto dropdown-list">
                    <li
                      role="button"
                      className={`${selectedSavefilterId === "" ? "active" : ""
                        } capitalize`}
                      onClick={() => {
                        onSetFilterData(initialFilterData);
                        onCallApiAgain(initialFilterData);
                        onSetSelectedSaveFilterId("");
                        setIsOpen(false);
                        localStorage.removeItem("filterData");
                        localStorage.removeItem("advanceFilterData");
                        localStorage.removeItem("selectedSavefilterId");
                      }}
                    >
                      Saved Filters
                    </li>

                    {filterOptions
                      ?.sort(
                        (a, b) => (a?.name || "").localeCompare(b?.name || "")
                      )
                      ?.map((el, idx) => {
                        const isSelected = selectedSavefilterId == el?.id;
                        return (
                          <li
                            role="button"
                            className={`${isSelected ? "active" : ""} capitalize flex items-center gap-2`}
                            key={idx}
                            onClick={() => {
                              handleSelectFilter(el?.searches);
                              onSetSelectedSaveFilterId(el?.id);
                              setIsOpen(false);
                              localStorage.setItem("selectedSavefilterId", el?.id);
                            }}
                          >
                            {el?.name}
                          </li>
                        );
                      })}
                  </ul>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      <SaveSearchModal
        from={from}
        filterType={filterType}
        showModal={showModal}
        saveSearchFilterData={saveSearchFilterData}
        onSetSelectedSaveFilterId={onSetSelectedSaveFilterId}
        onClose={() => setShowModal(false)}
      />

      <ManageSavedFiltersModal
        from={from}
        filterType={filterType}
        showModal={showManageModal}
        onClose={() => setShowManageModal(false)}
      />
    </>
  );
};

export default SavedFilterComponent;